import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, Car, Package } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface SearchResult {
  id: string;
  type: "customer" | "vehicle" | "tire_set";
  title: string;
  subtitle: string;
  path: string;
}

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SearchDialog = ({ open, onOpenChange }: SearchDialogProps) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!open) {
      setSearch("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const searchData = async () => {
      if (!search || search.length < 2 || !user) {
        setResults([]);
        return;
      }

      setLoading(true);
      const searchTerm = `*${search}*`;
      const allResults: SearchResult[] = [];

      try {
        // Search customers
        const { data: customers } = await supabase
          .from("customers")
          .select("id, name, email, phone")
          .or(`name.ilike.${searchTerm},email.ilike.${searchTerm},phone.ilike.${searchTerm}`)
          .limit(5);

        if (customers) {
          customers.forEach((customer) => {
            allResults.push({
              id: customer.id,
              type: "customer",
              title: customer.name,
              subtitle: customer.phone || customer.email || "",
              path: `/customers`,
            });
          });
        }

        // Search vehicles
        const { data: vehicles } = await supabase
          .from("vehicles")
          .select(`
            id, 
            reg_number, 
            make, 
            model,
            customers!inner(id, name)
          `)
          .or(`reg_number.ilike.${searchTerm},make.ilike.${searchTerm},model.ilike.${searchTerm}`)
          .limit(5);

        if (vehicles) {
          vehicles.forEach((vehicle: any) => {
            allResults.push({
              id: vehicle.id,
              type: "vehicle",
              title: vehicle.reg_number,
              subtitle: `${vehicle.make || ""} ${vehicle.model || ""} - ${vehicle.customers?.name || ""}`.trim(),
              path: `/customers`,
            });
          });
        }

        // Search tire sets
        const { data: tireSets } = await supabase
          .from("tire_sets")
          .select(`
            id,
            season,
            qr_code,
            customers!inner(id, name),
            vehicles!inner(id, reg_number)
          `)
          .or(`qr_code.ilike.${searchTerm}`)
          .limit(5);

        if (tireSets) {
          tireSets.forEach((tireSet: any) => {
            allResults.push({
              id: tireSet.id,
              type: "tire_set",
              title: `${tireSet.season} däck - ${tireSet.vehicles?.reg_number || ""}`,
              subtitle: tireSet.customers?.name || "",
              path: `/tires/${tireSet.id}`,
            });
          });
        }

        setResults(allResults);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchData, 300);
    return () => clearTimeout(timeoutId);
  }, [search, user]);

  const handleSelect = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "customer":
        return <User className="h-4 w-4 mr-2" />;
      case "vehicle":
        return <Car className="h-4 w-4 mr-2" />;
      case "tire_set":
        return <Package className="h-4 w-4 mr-2" />;
      default:
        return null;
    }
  };

  const getGroupLabel = (type: string) => {
    switch (type) {
      case "customer":
        return "Kunder";
      case "vehicle":
        return "Fordon";
      case "tire_set":
        return "Däck";
      default:
        return "";
    }
  };

  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 top-[8%] translate-y-0 sm:top-[10%]">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Sök efter kunder, fordon eller däck..." 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {!loading && search.length >= 2 && results.length === 0 && (
              <CommandEmpty>Inga resultat hittades.</CommandEmpty>
            )}
            {Object.entries(groupedResults).map(([type, items]) => (
              <CommandGroup key={type} heading={getGroupLabel(type)}>
                {items.map((result) => (
                  <CommandItem
                    key={result.id}
                    value={result.id}
                    onSelect={() => handleSelect(result.path)}
                    className="cursor-pointer"
                  >
                    {getIcon(result.type)}
                    <div className="flex flex-col">
                      <span className="font-medium">{result.title}</span>
                      <span className="text-sm text-muted-foreground">{result.subtitle}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
